import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Typography,
  Divider,
  Button,
  message,
} from "antd";
import { SettingOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  useGlobalSettings,
  GlobalSettings,
} from "../../hooks/useGlobalSettings";
import NumberInput from "./NumberInput";
// import { useChangeDetection } from "../../hooks/useChangeDetection";
import { useTender } from "../../contexts/TenderContext";

const { Title, Text } = Typography;
const { Option } = Select;

interface GlobalSettingsCardProps {
  inModal?: boolean;
  isViewMode?: boolean;
  onSave?: (settings: GlobalSettings) => void;
  onCancel?: () => void;
  selectedJobTypes?: {
    roadsAndSewers?: boolean;
    groundworks?: boolean;
  };
}

const GlobalSettingsCard: React.FC<GlobalSettingsCardProps> = ({
  inModal = false,
  isViewMode = false,
  onSave,
  onCancel,
  selectedJobTypes,
}) => {
  const {
    settings,
    updateSettings,
    updateRoadsAndSewersSettings,
    updateGroundworksSettings,
    resetSettings,
  } = useGlobalSettings();

  const { state, updateField, saveTender } = useTender();

  /* const {
    hasChanges: hasTrackedChanges,
    trackGlobalSettingsChange,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<GlobalSettings>({
    pageId: "global-settings",
    onChangeDetected: (changes) => {
      console.log("[GlobalSettings] Changes detected:", getChangeSummary());
    },
  }); */

  // Determine if only one job type is selected
  const showOnlyRoadsAndSewers =
    selectedJobTypes &&
    selectedJobTypes.roadsAndSewers &&
    !selectedJobTypes.groundworks;
  const showOnlyGroundworks =
    selectedJobTypes &&
    selectedJobTypes.groundworks &&
    !selectedJobTypes.roadsAndSewers;
  const showBothJobTypes =
    !selectedJobTypes ||
    (selectedJobTypes.roadsAndSewers && selectedJobTypes.groundworks);

  // For modal mode, keep a backup of original settings to enable cancel
  const [originalSettings, setOriginalSettings] =
    useState<GlobalSettings>(settings);
  // Local state for pending changes
  const [pendingSettings, setPendingSettings] =
    useState<GlobalSettings>(settings);
  const [hasChanges, setHasChanges] = useState(false);

  // Ex Topsoil Depth Utilised (Trial Pit Log) — this is the same value as
  // roadsData.cutFillSections.config.stripValueDefault (Cut & Fill's "Strip
  // Value (m)"), just surfaced here too; staged the same way as the rest of
  // this modal's fields so Cancel reverts it along with everything else.
  const stripValueDefault = state.roads.cutFillSections.config.stripValueDefault ?? 0.3;
  const [originalStripValue, setOriginalStripValue] = useState<number>(stripValueDefault);
  const [pendingStripValue, setPendingStripValue] = useState<number>(stripValueDefault);

  // Track previous inModal state to detect when modal opens
  const prevInModalRef = useRef(inModal);

  // Store original settings when modal opens (inModal goes from false to true)
  useEffect(() => {
    // Only reset when modal opens (inModal changes from false to true)
    if (inModal && !prevInModalRef.current) {
      setOriginalSettings(settings);
      setPendingSettings(settings);
      setOriginalStripValue(stripValueDefault);
      setPendingStripValue(stripValueDefault);
      setHasChanges(false);
    }
    prevInModalRef.current = inModal;
  }, [inModal, settings, stripValueDefault]);

  // Check if there are unsaved changes (for modal mode)
  useEffect(() => {
    if (inModal) {
      const changed =
        JSON.stringify(pendingSettings) !== JSON.stringify(originalSettings) ||
        pendingStripValue !== originalStripValue;
      setHasChanges(changed);
    }
  }, [pendingSettings, originalSettings, pendingStripValue, originalStripValue, inModal]);

  const handlePipeTypeChange = (value: "Clay" | "Plastic" | "Concrete") => {
    if (isViewMode) return;
    if (inModal) {
      // Update local pending state only
      setPendingSettings({
        ...pendingSettings,
        roadsAndSewers: {
          ...pendingSettings.roadsAndSewers,
          defaultPipeType: value,
        },
      });
    } else {
      // Update global state immediately for non-modal mode and track change
      /* trackGlobalSettingsChange(
        "roadsAndSewers.defaultPipeType",
        pendingSettings.roadsAndSewers.defaultPipeType,
        value
      ); */
      updateRoadsAndSewersSettings({ defaultPipeType: value });
    }
  };

  const handleBackfillTypeChange = (value: "Type 1" | "6F2") => {
    if (isViewMode) return;
    if (inModal) {
      setPendingSettings({
        ...pendingSettings,
        roadsAndSewers: {
          ...pendingSettings.roadsAndSewers,
          defaultBackfillType: value,
        },
      });
    } else {
      /* trackGlobalSettingsChange(
        "roadsAndSewers.defaultBackfillType",
        pendingSettings.roadsAndSewers.defaultBackfillType,
        value
      ); */
      updateRoadsAndSewersSettings({ defaultBackfillType: value });
    }
  };

  const handleRoadsOHPChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          roadsAndSewers: {
            ...pendingSettings.roadsAndSewers,
            overheadAndProfit: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "roadsAndSewers.overheadAndProfit",
          pendingSettings.roadsAndSewers.overheadAndProfit,
          value
        ); */
        updateRoadsAndSewersSettings({ overheadAndProfit: value });
      }
    }
  };

  const handleRoadsPreliminariesChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          roadsAndSewers: {
            ...pendingSettings.roadsAndSewers,
            preliminaries: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "roadsAndSewers.preliminaries",
          pendingSettings.roadsAndSewers.preliminaries,
          value
        ); */
        updateRoadsAndSewersSettings({ preliminaries: value });
      }
    }
  };

  const handleRoadsDiscountChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          roadsAndSewers: {
            ...pendingSettings.roadsAndSewers,
            discount: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "roadsAndSewers.discount",
          pendingSettings.roadsAndSewers.discount,
          value
        ); */
        updateRoadsAndSewersSettings({ discount: value });
      }
    }
  };

  const updateStripValueDefault = (value: number) => {
    updateField("roads", {
      ...state.roads,
      cutFillSections: {
        ...state.roads.cutFillSections,
        config: { ...state.roads.cutFillSections.config, stripValueDefault: value },
      },
    });
  };

  const handleExTopsoilDepthChange = (value: number) => {
    if (isViewMode) return;
    if (value < 0) return;
    if (inModal) {
      setPendingStripValue(value);
    } else {
      updateStripValueDefault(value);
    }
  };

  const handleGroundworksOHPChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          groundworks: {
            ...pendingSettings.groundworks,
            overheadAndProfit: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "groundworks.overheadAndProfit",
          pendingSettings.groundworks.overheadAndProfit,
          value
        ); */
        updateGroundworksSettings({ overheadAndProfit: value });
      }
    }
  };

  const handleGroundworksPreliminariesChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          groundworks: {
            ...pendingSettings.groundworks,
            preliminaries: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "groundworks.preliminaries",
          pendingSettings.groundworks.preliminaries,
          value
        ); */
        updateGroundworksSettings({ preliminaries: value });
      }
    }
  };

  const handleGroundworksDiscountChange = (value: number) => {
    if (isViewMode) return;
    if (value >= 0 && value <= 100) {
      if (inModal) {
        setPendingSettings({
          ...pendingSettings,
          groundworks: {
            ...pendingSettings.groundworks,
            discount: value,
          },
        });
      } else {
        /* trackGlobalSettingsChange(
          "groundworks.discount",
          pendingSettings.groundworks.discount,
          value
        ); */
        updateGroundworksSettings({ discount: value });
      }
    }
  };

  const handleSave = async () => {
    if (isViewMode) return;
    if (inModal) {
      // Track all changes between original and pending settings
      /* if (
        originalSettings.roadsAndSewers.defaultPipeType !==
        pendingSettings.roadsAndSewers.defaultPipeType
      ) {
        trackGlobalSettingsChange(
          "roadsAndSewers.defaultPipeType",
          originalSettings.roadsAndSewers.defaultPipeType,
          pendingSettings.roadsAndSewers.defaultPipeType
        );
      }
      if (
        originalSettings.roadsAndSewers.defaultBackfillType !==
        pendingSettings.roadsAndSewers.defaultBackfillType
      ) {
        trackGlobalSettingsChange(
          "roadsAndSewers.defaultBackfillType",
          originalSettings.roadsAndSewers.defaultBackfillType,
          pendingSettings.roadsAndSewers.defaultBackfillType
        );
      }
      if (
        originalSettings.roadsAndSewers.overheadAndProfit !==
        pendingSettings.roadsAndSewers.overheadAndProfit
      ) {
        trackGlobalSettingsChange(
          "roadsAndSewers.overheadAndProfit",
          originalSettings.roadsAndSewers.overheadAndProfit,
          pendingSettings.roadsAndSewers.overheadAndProfit
        );
      }
      if (
        originalSettings.roadsAndSewers.preliminaries !==
        pendingSettings.roadsAndSewers.preliminaries
      ) {
        trackGlobalSettingsChange(
          "roadsAndSewers.preliminaries",
          originalSettings.roadsAndSewers.preliminaries,
          pendingSettings.roadsAndSewers.preliminaries
        );
      }
      if (
        originalSettings.roadsAndSewers.discount !==
        pendingSettings.roadsAndSewers.discount
      ) {
        trackGlobalSettingsChange(
          "roadsAndSewers.discount",
          originalSettings.roadsAndSewers.discount,
          pendingSettings.roadsAndSewers.discount
        );
      }
      if (
        originalSettings.groundworks.overheadAndProfit !==
        pendingSettings.groundworks.overheadAndProfit
      ) {
        trackGlobalSettingsChange(
          "groundworks.overheadAndProfit",
          originalSettings.groundworks.overheadAndProfit,
          pendingSettings.groundworks.overheadAndProfit
        );
      }
      if (
        originalSettings.groundworks.preliminaries !==
        pendingSettings.groundworks.preliminaries
      ) {
        trackGlobalSettingsChange(
          "groundworks.preliminaries",
          originalSettings.groundworks.preliminaries,
          pendingSettings.groundworks.preliminaries
        );
      }
      if (
        originalSettings.groundworks.discount !==
        pendingSettings.groundworks.discount
      ) {
        trackGlobalSettingsChange(
          "groundworks.discount",
          originalSettings.groundworks.discount,
          pendingSettings.groundworks.discount
        );
      } */

      // Apply pending settings to global state
      updateSettings(pendingSettings);
      setOriginalSettings(pendingSettings);
      if (pendingStripValue !== originalStripValue) {
        updateStripValueDefault(pendingStripValue);
      }
      setOriginalStripValue(pendingStripValue);
      setHasChanges(false);

      // Wait for state to propagate before saving to API
      console.log(
        "🔧 Global settings updated, waiting for state propagation..."
      );
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Immediately save to API
      try {
        console.log(
          "💾 Calling saveTender to persist global settings to API..."
        );
        await saveTender();
        console.log("✅ Global settings saved to API successfully");
        message.success("Global settings saved successfully");
      } catch (error) {
        console.error("❌ Error saving global settings to API:", error);
        message.warning("Global settings saved locally only");
      }

      onSave?.(pendingSettings);
    } else {
      // For non-modal mode, settings are already applied
      // Wait for state to propagate
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        console.log(
          "💾 Calling saveTender to persist global settings to API..."
        );
        await saveTender();
        console.log("✅ Global settings saved to API successfully");
        message.success("Global settings saved successfully");
      } catch (error) {
        console.error("❌ Error saving global settings to API:", error);
        message.warning("Global settings saved locally only");
      }
      onSave?.(settings);
    }
  };

  const handleCancel = () => {
    if (inModal) {
      // Restore original settings (revert pending changes)
      setPendingSettings(originalSettings);
      setPendingStripValue(originalStripValue);
      setHasChanges(false);
      // resetChanges(); // Reset changes only when canceling (reverting to original)
    }
    onCancel?.();
  };

  // Use pending settings in modal mode, actual settings otherwise
  const displaySettings = inModal ? pendingSettings : settings;
  const displayStripValue = inModal ? pendingStripValue : stripValueDefault;

  const handleReset = async () => {
    if (isViewMode) return;
    const defaultSettings = {
      roadsAndSewers: {
        defaultPipeType: "Plastic" as const,
        defaultBackfillType: "Type 1" as const,
        overheadAndProfit: 0,
        preliminaries: 0,
        discount: 0,
      },
      groundworks: {
        overheadAndProfit: 0,
        preliminaries: 0,
        discount: 0,
      },
    };

    if (inModal) {
      // In modal mode, update pending settings only
      setPendingSettings(defaultSettings);
      setHasChanges(
        JSON.stringify(defaultSettings) !== JSON.stringify(originalSettings)
      );
      message.success(
        "Settings reset to default values. Click 'Save Settings' to apply."
      );
    } else {
      // In non-modal mode, track changes and reset immediately
      /* trackGlobalSettingsChange(
        "groundworks.overheadAndProfit",
        settings.groundworks.overheadAndProfit,
        0
      );
      trackGlobalSettingsChange(
        "groundworks.preliminaries",
        settings.groundworks.preliminaries,
        0
      );
      trackGlobalSettingsChange(
        "groundworks.discount",
        settings.groundworks.discount,
        0
      );
      trackGlobalSettingsChange(
        "roadsAndSewers.overheadAndProfit",
        settings.roadsAndSewers.overheadAndProfit,
        0
      );
      trackGlobalSettingsChange(
        "roadsAndSewers.preliminaries",
        settings.roadsAndSewers.preliminaries,
        0
      );
      trackGlobalSettingsChange(
        "roadsAndSewers.discount",
        settings.roadsAndSewers.discount,
        0
      );
      trackGlobalSettingsChange(
        "roadsAndSewers.defaultPipeType",
        settings.roadsAndSewers.defaultPipeType,
        "Plastic"
      );
      trackGlobalSettingsChange(
        "roadsAndSewers.defaultBackfillType",
        settings.roadsAndSewers.defaultBackfillType,
        "Type 1"
      ); */
      resetSettings();

      // Wait for state to propagate
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Immediately save to API
      try {
        console.log("💾 Saving reset settings to API...");
        await saveTender();
        console.log("✅ Settings reset and saved to API successfully");
        message.success("Settings reset to default values");
      } catch (error) {
        console.error("❌ Error saving reset settings to API:", error);
        message.warning("Settings reset locally only");
      }
    }
  };

  const content = (
    <div className={inModal ? "py-2" : ""}>
      {!inModal && (
        <div className="flex items-center mb-6">
          <SettingOutlined className="mr-3 text-blue-600 text-xl" />
          <Title level={4} className="mb-0 text-blue-900">
            Global Settings
          </Title>
        </div>
      )}

      {inModal && (
        <div className="mb-6">
          <Text className="text-gray-600 text-base">
            Configure default values that will be applied throughout the
            application. These settings are saved and are specific to each job
            type.
          </Text>
        </div>
      )}

      {!inModal && (
        <div className="mb-4 text-center">
          <Text className="text-gray-600 text-base">
            Configure default values that will be applied throughout the
            application. These settings are saved automatically and are specific
            to each job type.
          </Text>
        </div>
      )}

      {/* Roads & Sewers Settings */}
      {(!selectedJobTypes || selectedJobTypes.roadsAndSewers) && (
        <div
          className={`${
            inModal ? "mb-6 pb-6 border-b border-gray-200" : "mb-8"
          }`}
        >
          {showBothJobTypes && (
            <div className="text-center mb-6">
              <Title level={5} className="text-orange-700 mb-2">
                🛣️ Roads & Sewers Settings
              </Title>
            </div>
          )}

          <div className="space-y-6">
          <div className="mt-2 border-t border-gray-200">
                       <div >
                  <Text
                    strong
                    className="text-gray-800 text-sm uppercase tracking-wide"
                  >
                    Ex Topsoil Depth Utilised
                  </Text>
                  <NumberInput
                    value={displayStripValue}
                    onChange={handleExTopsoilDepthChange}
                    decimalPlaces={3}
                    positiveOnly
                    zeroIntegerOnly
                    className="w-full"
                    size="large"
                    placeholder="0.300"
                    style={{ borderRadius: "8px", textAlign: "left" }}
                    disabled={isViewMode}
                  />
                </div>
            </div>
            

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  OH&P (%)
                </Text>
                <NumberInput
                  value={displaySettings.roadsAndSewers.overheadAndProfit}
                  onChange={handleRoadsOHPChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  Prelims (%)
                </Text>
                <NumberInput
                  value={displaySettings.roadsAndSewers.preliminaries}
                  onChange={handleRoadsPreliminariesChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  Discount (%)
                </Text>
                <NumberInput
                  value={displaySettings.roadsAndSewers.discount}
                  onChange={handleRoadsDiscountChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>
            </div>

            
          </div>
        </div>
      )}

      {/* Groundworks Settings */}
      {(!selectedJobTypes || selectedJobTypes.groundworks) && (
        <div className={`${inModal ? "mb-6 pb-6" : "mb-8"}`}>
          {showBothJobTypes && (
            <div className="text-center mb-6">
              <Title level={5} className="text-blue-700 mb-2">
                🏗️ Groundworks Settings
              </Title>
            </div>
          )}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  OH&P (%)
                </Text>
                <NumberInput
                  value={displaySettings.groundworks.overheadAndProfit}
                  onChange={handleGroundworksOHPChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  Prelims (%)
                </Text>
                <NumberInput
                  value={displaySettings.groundworks.preliminaries}
                  onChange={handleGroundworksPreliminariesChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-3">
                <Text
                  strong
                  className="text-gray-800 text-sm uppercase tracking-wide"
                >
                  Discount (%)
                </Text>
                <NumberInput
                  value={displaySettings.groundworks.discount}
                  onChange={handleGroundworksDiscountChange}
                  decimalPlaces={2}
                  className="w-full"
                  size="large"
                  placeholder="0.00"
                  style={{ borderRadius: "8px", textAlign: "left" }}
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Button */}
      {!inModal && (
        <div className="text-center mb-8">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            size="large"
            className="border-red-200 text-red-600 hover:border-red-300 hover:text-red-700 hover:bg-red-50 px-8 py-2 rounded-lg shadow-sm"
            style={{
              borderRadius: "12px",
              fontWeight: 500,
              letterSpacing: "0.5px",
            }}
          >
            Reset to Default
          </Button>
        </div>
      )}

      {!inModal && <Divider className="my-6" />}

      {!inModal && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-center mb-4">
            <Text strong className="text-gray-700 text-lg">
              {showOnlyRoadsAndSewers
                ? "📊 Current Roads & Sewers Settings"
                : showOnlyGroundworks
                ? "📊 Current Groundworks Settings"
                : "📊 Current Settings Summary"}
            </Text>
          </div>

          {/* Roads & Sewers Status */}
          {(!selectedJobTypes || selectedJobTypes.roadsAndSewers) && (
            <div
              className={`${showBothJobTypes ? "mb-6" : ""} ${
                inModal ? "bg-white p-4 rounded-lg shadow-sm" : ""
              }`}
            >
              {showBothJobTypes && (
                <div className="text-center mb-3">
                  <Text strong className="text-orange-600 text-base">
                    🛣️ Roads & Sewers
                  </Text>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Pipe Type
                  </Text>
                  <Text strong className="text-orange-600 text-sm">
                    {settings.roadsAndSewers.defaultPipeType}
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Backfill
                  </Text>
                  <Text strong className="text-orange-600 text-sm">
                    {settings.roadsAndSewers.defaultBackfillType}
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    OH&P
                  </Text>
                  <Text strong className="text-orange-600 text-sm">
                    {settings.roadsAndSewers.overheadAndProfit}%
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Prelims
                  </Text>
                  <Text strong className="text-orange-600 text-sm">
                    {settings.roadsAndSewers.preliminaries}%
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Discount
                  </Text>
                  <Text strong className="text-orange-600 text-sm">
                    {settings.roadsAndSewers.discount}%
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Groundworks Status */}
          {(!selectedJobTypes || selectedJobTypes.groundworks) && (
            <div
              className={`${
                inModal ? "bg-white p-4 rounded-lg shadow-sm" : ""
              }`}
            >
              {showBothJobTypes && (
                <div className="text-center mb-3">
                  <Text strong className="text-blue-600 text-base">
                    🏗️ Groundworks
                  </Text>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    OH&P
                  </Text>
                  <Text strong className="text-blue-600 text-sm">
                    {settings.groundworks.overheadAndProfit}%
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Prelims
                  </Text>
                  <Text strong className="text-blue-600 text-sm">
                    {settings.groundworks.preliminaries}%
                  </Text>
                </div>
                <div className="text-center">
                  <Text className="text-gray-500 text-xs uppercase tracking-wide block mb-1">
                    Discount
                  </Text>
                  <Text strong className="text-blue-600 text-sm">
                    {settings.groundworks.discount}%
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {inModal && (
        <div
          className="flex justify-between items-center gap-2 mt-6"
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: "16px",
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
            marginBottom: "-20px",
            paddingBottom: "20px",
          }}
        >
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            danger
            type="text"
            disabled={isViewMode}
          >
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!hasChanges || isViewMode}
              style={{
                backgroundColor: !hasChanges || isViewMode ? undefined : "#1890ff",
                borderColor: !hasChanges || isViewMode ? undefined : "#1890ff",
              }}
            >
              Save Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (inModal) {
    return content;
  }

  return (
    <Card
      className="shadow-lg mb-6"
      // title={
      //   <div className="flex items-center">
      //     <SettingOutlined className="mr-2 mt-4 text-blue-600" />
      //     <Title level={4} className="mb-0 text-blue-900">
      //       Global Settings
      //     </Title>
      //   </div>
      // }
      // extra={
      //   <Button
      //     icon={<ReloadOutlined />}
      //     onClick={handleReset}
      //     type="text"
      //     className="text-gray-500 hover:text-blue-600"
      //   >
      //     Reset to Default
      //   </Button>
      // }
    >
      {content}
    </Card>
  );
};

export default GlobalSettingsCard;
